import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Share } from '@capacitor/share';
import { BehaviorSubject, Observable } from 'rxjs';
import { DeviceInfo, UserContextInfo } from '../../interfaces/master-interfaces';







@Injectable({
  providedIn: 'root'
})


export class DeviceService {

  private userAgent: string;
  private isBrowser: boolean;

  // New: Context tracking
  private contextSubject = new BehaviorSubject<UserContextInfo | null>(null);
  public context$ = this.contextSubject.asObservable();

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.userAgent = this.isBrowser ? window.navigator.userAgent : '';
  }

  setUserContext(context: UserContextInfo): void {
    console.log('📱 DeviceService: Setting user context:', context);
    this.contextSubject.next(context);
  }

  getCurrentContext(): UserContextInfo | null {
    return this.contextSubject.value;
  }

  isFromWhatsApp(): boolean {
    return this.getCurrentContext()?.isFromWhatsApp ?? false;
  }

  getWhatsAppDetectionMethod(): string | null {
    return this.getCurrentContext()?.whatsAppDetectionMethod ?? null;
  }

  getConversationInfo(): { id: string | null; creator: string | null } {
    const context = this.getCurrentContext();
    return {
      id: context?.conversationId ?? null,
      creator: context?.creator ?? null
    };
  }

  isIPhone(): boolean {
    return this.getCurrentContext()?.isIPhone ?? /iPhone/.test(this.userAgent);
  }

  isIOS(): boolean {
    return this.getCurrentContext()?.isIOS ?? /iPad|iPhone|iPod/.test(this.userAgent);
  }

  isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  needsSafeArea(): boolean {
    return this.isIOS() && this.isStandalone();
  }

  isStandalone(): boolean {
    return (window as any).navigator.standalone === true;
  }

  getContext(): Observable<UserContextInfo | null> {
    return this.context$;
  }


  clearContext(): void {
    console.log('🧹 DeviceService: Clearing user context');
    this.contextSubject.next(null);
  }


  getDeviceInfo(): DeviceInfo {
    if (!this.isBrowser) {
      return {
        type: 'unknown',
        model: 'unknown',
        os: 'unknown',
        browser: 'unknown'
      };
    }

    const deviceType = this.getDeviceType();
    const deviceModel = this.getDeviceModel();
    const operatingSystem = this.getOperatingSystem();
    const browserInfo = this.getBrowserInfo();

    return {
      type: deviceType,
      model: deviceModel,
      os: operatingSystem,
      browser: browserInfo
    };
  }

  getDeviceType(): string {
    const tablets = [
      /ipad/i,
      /tablet/i,
      /kindle/i,
      /playbook/i,
      /silk/i
    ];

    const phones = [
      /android.*mobile/i,
      /iphone/i,
      /windows phone/i,
      /blackberry/i,
      /opera mini/i
    ];

    if (tablets.some(tablet => tablet.test(this.userAgent))) {
      return 'tablet';
    } else if (phones.some(phone => phone.test(this.userAgent))) {
      return 'phone';
    } else {
      return 'desktop';
    }
  }

  isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }


  detectPlatform(): string {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Mac/.test(ua)) return 'Mac';
    return 'Unknown';
  }


  isSystemShareReliable(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();

    // Known problematic scenarios
    const isPWAStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isPWAMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
    const isPWAFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const isPWA = isPWAStandalone || isPWAMinimalUI || isPWAFullscreen;

    const isIOSPWA = isPWA && /iphone|ipad/.test(userAgent);
    const isAndroidPWA = isPWA && /android/.test(userAgent);

    // Desktop browsers (except Safari) often have unreliable share
    const isDesktop = !(/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent));
    const isDesktopChrome = isDesktop && /chrome/.test(userAgent) && !/edge|opr|opera/.test(userAgent);
    const isDesktopFirefox = isDesktop && /firefox/.test(userAgent);

    // Problematic combinations
    if (isIOSPWA || isAndroidPWA) {
      console.log('🚫 PWA detected on mobile, share API unreliable');
      return false;
    }

    if (isDesktopChrome || isDesktopFirefox) {
      console.log('🚫 Desktop browser detected, share API often not available');
      return false;
    }

    // Only trust it on mobile Safari and Android Chrome (not in PWA mode)
    const isMobileSafari = !isPWA && /iphone|ipad/.test(userAgent) && /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
    const isMobileAndroidChrome = !isPWA && /android/.test(userAgent) && /chrome/.test(userAgent) && !/edge|opr|opera/.test(userAgent);

    if (isMobileSafari || isMobileAndroidChrome) {
      // Even then, check if the API actually exists and works
      const hasWebShare = navigator.share && typeof navigator.share === 'function';
      const hasCanShare = navigator.canShare && typeof navigator.canShare === 'function';
      const hasCapacitorShare = Share && Share.share && typeof Share.share === 'function';

      if (hasCapacitorShare) {
        console.log('✅ Capacitor Share available');
        return true;
      }

      if (hasWebShare && hasCanShare) {
        console.log('✅ Web Share API with canShare available');
        return true;
      }

      if (hasWebShare) {
        console.log('⚠️ Web Share API available but no canShare method');
        return true; // Still worth trying
      }
    }

    console.log('🚫 System share not reliable for this browser/mode');
    return false;
  }

  private getDeviceModel(): string {
    // iPhone detection
    const iPhoneMatch = this.userAgent.match(/iPhone\s*(?:OS\s*)?(\d+)/i);
    if (iPhoneMatch) {
      return `iPhone ${iPhoneMatch[1]}`;
    }

    // iPad detection
    const iPadMatch = this.userAgent.match(/iPad.*OS\s*(\d+)/i);
    if (iPadMatch) {
      return `iPad ${iPadMatch[1]}`;
    }

    // Samsung detection
    const samsungMatch = this.userAgent.match(/SM-[A-Z]\d{3,4}/i);
    if (samsungMatch) {
      return samsungMatch[0];
    }

    // Google Pixel detection
    const pixelMatch = this.userAgent.match(/Pixel\s*\d/i);
    if (pixelMatch) {
      return pixelMatch[0];
    }

    return 'unknown';
  }

  isIOSPWA(): boolean {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    return isIOS && isPWA;
  }

  private getOperatingSystem(): string {
    const osPatterns = {
      ios: /ip[honead]+(?:.*os\s*([\w]+)*\slike\smac|;\sopera)/i,
      android: /android/i,
      windows: /windows nt/i,
      mac: /macintosh|mac os x/i,
      linux: /linux/i
    };

    for (const [os, pattern] of Object.entries(osPatterns)) {
      if (pattern.test(this.userAgent)) {
        return os;
      }
    }

    return 'unknown';
  }

  detectIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad on iOS 13+
  }

  isIOSSafari(): boolean {
    const userAgent = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(userAgent) &&
      userAgent.includes('Safari') &&
      !userAgent.includes('Chrome');
  }

  private getBrowserInfo(): string {
    const browserPatterns = {
      chrome: /chrome|chromium|crios/i,
      firefox: /firefox|fxios/i,
      safari: /safari/i,
      edge: /edge/i,
      opera: /opera|opr/i
    };

    // Chrome needs to be checked last as other browsers may include 'Chrome' in their UA
    for (const [browser, pattern] of Object.entries(browserPatterns)) {
      if (pattern.test(this.userAgent) && browser !== 'chrome') {
        return browser;
      }
    }

    if (browserPatterns.chrome.test(this.userAgent)) {
      return 'chrome';
    }

    return 'unknown';
  }
}